import { useTranslation } from "react-i18next";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import useStyles from "./styles";
import { FC, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { CustomButton } from "../../../components/CustomButton/CustomButton";
import { CustomTextInput } from "../../../components/CustomTextInput/CustomTextInput";
import { colors } from "../../../styles/colors";
import {
  useArchiveCompanie,
  useCompanyDeletionImpact,
  useDeleteCompanie,
  useRestoreCompanie,
} from "../../../api/companies";

/**
 * Which action the operator picked in the card menu. The dialog does not guess:
 * archiving and deleting have different consequences, so the choice is made
 * before the dialog opens and the dialog only confirms it.
 */
export type TSocietyAction = "archive" | "restore" | "delete";

type props = {
  open: boolean;
  companie: TCompanie;
  /** Defaults to "delete" so existing call sites keep their old behaviour. */
  intent?: TSocietyAction;
  onCancel?: () => void;
  onClose?: () => void;
};

/**
 * Company removal dialog.
 *
 * This used to be a bare "are you sure?" wired straight to DELETE. That was
 * dangerous: in the schema almost every foreign key on `company` cascades, so
 * confirming here could take the tenant's vehicles, rental contracts and fleet
 * cards with it. The only reason it never did is that a session foreign key
 * happened to block the statement, which surfaced to the operator as a raw SQL
 * error.
 *
 * The dialog now asks the API what would actually happen first:
 *   - blockers present  -> deletion is off the table, archiving is the action
 *   - nothing blocking  -> show the collateral, and require the operator to
 *                          type the company name before the destructive button
 *                          becomes available
 *
 * It now serves all three lifecycle actions, selected by `intent` in the card
 * menu. Archive and restore are cheap and reversible, so they confirm in one
 * click; only "delete" pays for the impact dry run and the typed confirmation.
 */
export const ModalDeleteSociety: FC<props> = ({
  open,
  companie,
  intent = "delete",
  onCancel,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState("");

  // Only the delete flow needs the dry run. Archiving and restoring cannot
  // destroy anything, so they should not cost a round trip or block on it.
  const {
    impact,
    isLoading,
    isError,
  } = useCompanyDeletionImpact(companie.id, open && intent === "delete");

  const { mutate: deleteCompanie, isPending: isDeleting } = useDeleteCompanie(
    companie.id,
    onClose
  );
  const { mutate: archiveCompanie, isPending: isArchiving } =
    useArchiveCompanie(companie.id, onClose);
  const { mutate: restoreCompanie, isPending: isRestoring } =
    useRestoreCompanie(companie.id, onClose);

  const blockers = useMemo(
    () => Object.entries(impact?.blockers ?? {}),
    [impact]
  );
  const collateral = useMemo(
    () => Object.entries(impact?.collateral ?? {}),
    [impact]
  );

  const canPurge = impact?.can_purge === true;
  const confirmMatches = confirmName.trim() === companie.name;
  const busy = isDeleting || isArchiving || isRestoring;

  // The typed confirmation is a one-shot gate. Leaving a stale match in state
  // after the dialog closes would arm the destructive button the next time it
  // opens, possibly against a different company on the same card list.
  useEffect(() => {
    if (!open) setConfirmName("");
  }, [open]);

  // Labels come back as snake_case keys ("rent_contracts_as_lessee"). Render
  // them readably without needing a translation entry per dependent table.
  const humanize = (label: string) => label.replace(/_/g, " ");

  return (
    <CustomModal
      open={open}
      onCancel={onCancel}
      title={""}
      onClose={onClose}
      isConfirmation
      showButtonAction={false}
    >
      <div className={classes.textModalContainer}>
        {intent === "archive" && (
          <>
            <CustomTypography
              text={t("compagnies.archiveTitle", {
                name: companie.name,
                defaultValue: `Archive "${companie.name}"?`,
              })}
              variant="default"
              fontWeight="bold"
            />
            <CustomTypography
              text={t(
                "compagnies.archiveBody",
                "The company and its members are deactivated and signed out immediately. Nothing is erased, and you can restore it at any time."
              )}
              variant="default"
            />
          </>
        )}

        {intent === "restore" && (
          <>
            <CustomTypography
              text={t("compagnies.restoreTitle", {
                name: companie.name,
                defaultValue: `Restore "${companie.name}"?`,
              })}
              variant="default"
              fontWeight="bold"
            />
            <CustomTypography
              text={t(
                "compagnies.restoreBody",
                "The company becomes active again. Its members keep the roles they had before archiving and will need to sign in again."
              )}
              variant="default"
            />
          </>
        )}

        {intent === "delete" && isLoading && (
          <div className={classes.loaderRow}>
            <CircularProgress size={28} />
          </div>
        )}

        {intent === "delete" && isError && (
          <CustomTypography
            text={t(
              "compagnies.impactUnavailable",
              "Could not check what this company holds. Archive it instead, or try again."
            )}
            variant="default"
            color={colors.error}
          />
        )}

        {intent === "delete" && impact && (
          <>
            {blockers.length > 0 ? (
              <>
                <CustomTypography
                  text={t("compagnies.cannotDeleteTitle", {
                    name: companie.name,
                    defaultValue: `"${companie.name}" cannot be deleted.`,
                  })}
                  variant="default"
                  fontWeight="bold"
                />
                <CustomTypography
                  text={t(
                    "compagnies.cannotDeleteBody",
                    "It still holds records that must be kept. Archiving deactivates the company and signs out its users, and can be undone."
                  )}
                  variant="default"
                />
                <ul
                  className={`${classes.impactList} ${classes.impactBlocking}`}
                >
                  {blockers.map(([label, count]) => (
                    <li key={label} className={classes.impactRow}>
                      <CustomTypography
                        text={humanize(label)}
                        variant="default"
                      />
                      <CustomTypography
                        text={String(count)}
                        variant="default"
                        fontWeight="bold"
                      />
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <CustomTypography
                  text={t("compagnies.deleteMessage", {
                    name: companie.name,
                  })}
                  variant="default"
                />

                {collateral.length > 0 && (
                  <>
                    <CustomTypography
                      text={t(
                        "compagnies.deleteCollateral",
                        "The following will be permanently removed with it:"
                      )}
                      variant="default"
                    />
                    <ul
                      className={`${classes.impactList} ${classes.impactCollateral}`}
                    >
                      {collateral.map(([label, count]) => (
                        <li key={label} className={classes.impactRow}>
                          <CustomTypography
                            text={humanize(label)}
                            variant="default"
                          />
                          <CustomTypography
                            text={String(count)}
                            variant="default"
                            fontWeight="bold"
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <div className={classes.confirmField}>
                  <CustomTextInput
                    label={t("compagnies.typeNameToConfirm", {
                      name: companie.name,
                      defaultValue: `Type "${companie.name}" to confirm`,
                    })}
                    placeholder={companie.name}
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className={classes.actions}>
        <CustomButton
          label={t("common.cancel", "Cancel")}
          variant="default"
          textVariant="link"
          fontWeight="bold"
          width="fit-content"
          padding="3px 16px"
          onClick={onCancel}
          disabled={busy}
        />

        {intent === "restore" && (
          <CustomButton
            label={t("compagnies.restore", "Restore")}
            variant="primary"
            textVariant="link"
            fontWeight="bold"
            width="fit-content"
            padding="3px 16px"
            onClick={() => restoreCompanie()}
            loading={isRestoring}
            disabled={busy}
          />
        )}

        {/*
          Archive is offered for its own intent, and again as the way out of a
          refused delete — that second case is the whole point of the blocker
          panel, so the operator should not have to close and reopen to get it.
          Hidden once the company is already archived.
        */}
        {(intent === "archive" ||
          (intent === "delete" && impact?.is_active !== false)) && (
          <CustomButton
            label={t("compagnies.archive", "Archive")}
            variant={intent === "archive" || !canPurge ? "secondary" : "white"}
            textVariant="link"
            fontWeight="bold"
            width="fit-content"
            padding="3px 16px"
            onClick={() => archiveCompanie()}
            loading={isArchiving}
            disabled={busy || (intent === "delete" && !impact)}
          />
        )}

        {intent === "delete" && canPurge && (
          <CustomButton
            label={t("compagnies.deletePermanently", "Delete permanently")}
            variant="secondary"
            textVariant="link"
            fontWeight="bold"
            width="fit-content"
            padding="3px 16px"
            onClick={() => deleteCompanie(confirmName.trim())}
            loading={isDeleting}
            disabled={busy || !confirmMatches}
          />
        )}
      </div>
    </CustomModal>
  );
};
